import React, { useState } from 'react';
import axios from 'axios';
import { toast, Toaster } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

// LIOR quick-order PWA (May 2026).
// Single-purpose page intended to be "Add to Home Screen" on Liʼor's phone.
// Layout: dark gym vibe — neon orange accent, large angular LIOR mark,
// noise grain overlay, oversized inputs friendly for thumb typing.
// Submits to /api/orders/lior-quick which writes a tagged order
// (source=lior_link, sent_by=ליאור) straight into the admin dashboard.

const fields = [
  { key: 'customer_name', label: 'שם מלא', placeholder: 'ישראל ישראלי', autoComplete: 'name', required: true },
  { key: 'customer_phone', label: 'טלפון', placeholder: '050-0000000', autoComplete: 'tel', type: 'tel', dir: 'ltr', required: true },
  { key: 'customer_address', label: 'כתובת מלאה', placeholder: 'רחוב, מספר, עיר', autoComplete: 'street-address', required: true },
  { key: 'notes', label: 'הערות (לא חובה)', placeholder: 'מה לרשום על המשלוח?', textarea: true, required: false },
];

const LiorOrder = () => {
  const [form, setForm] = useState({ customer_name: '', customer_phone: '', customer_address: '', notes: '' });
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.customer_name.trim() || !form.customer_phone.trim() || !form.customer_address.trim()) {
      toast.error('חסרים פרטים');
      return;
    }
    setSubmitting(true);
    try {
      const r = await axios.post(`${API}/orders/lior-quick`, form);
      setSuccess(r.data);
      setForm({ customer_name: '', customer_phone: '', customer_address: '', notes: '' });
      // Subtle haptic feedback if available — feels app-native on mobile.
      if (navigator.vibrate) navigator.vibrate([40, 30, 40]);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'שגיאה בשליחה');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen text-white overflow-x-hidden" dir="rtl" style={{
      background: 'radial-gradient(circle at top right, #1a0f08 0%, #0a0604 55%, #000 100%)',
      fontFamily: 'Heebo, system-ui, sans-serif',
    }} data-testid="lior-page">
      <Toaster richColors position="top-center" />
      {/* Subtle grain texture overlay */}
      <div className="pointer-events-none fixed inset-0 z-0 opacity-[0.08]" style={{
        backgroundImage: "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='100' height='100'><filter id='n'><feTurbulence baseFrequency='0.85' numOctaves='3'/><feColorMatrix values='0 0 0 0 1  0 0 0 0 0.45  0 0 0 0 0.05  0 0 0 0.8 0'/></filter><rect width='100' height='100' filter='url(%23n)'/></svg>\")",
      }} />

      <div className="relative z-10 max-w-md mx-auto px-5 pt-10 pb-12">
        {/* Brand mark */}
        <div className="flex flex-col items-center mb-8" data-testid="lior-logo">
          <div className="relative">
            <div className="absolute inset-0 blur-2xl opacity-50" style={{ background: '#ff6a13' }}></div>
            <h1 className="relative text-7xl font-black tracking-tighter" style={{
              fontFamily: '"Bebas Neue", "Heebo", sans-serif',
              background: 'linear-gradient(180deg,#ff8a3d 0%,#ff5b00 60%,#9a3000 100%)',
              WebkitBackgroundClip: 'text',
              backgroundClip: 'text',
              color: 'transparent',
              letterSpacing: '0.02em',
              textShadow: '0 4px 30px rgba(255,90,0,0.35)',
            }}>LIOR</h1>
          </div>
          <p className="mt-2 text-[11px] uppercase tracking-[0.4em] text-amber-200/80">FITNESS · QUICK ORDER</p>
          <div className="mt-4 w-20 h-[2px] bg-gradient-to-l from-transparent via-orange-500 to-transparent" />
        </div>

        {success ? (
          // Success card — replaces the form. Big confirmation, ability to send another.
          <div className="bg-gradient-to-br from-emerald-900/40 to-emerald-950/60 border border-emerald-500/30 rounded-3xl p-7 text-center backdrop-blur-xl" data-testid="lior-success">
            <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-emerald-500/15 border-2 border-emerald-400 mb-4">
              <svg className="w-10 h-10 text-emerald-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}><path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" /></svg>
            </div>
            <h2 className="text-2xl font-black mb-1">ההזמנה נשלחה</h2>
            <p className="text-emerald-200/80 text-sm mb-1">מספר הזמנה</p>
            <p className="text-3xl font-black text-emerald-300 mb-5 tracking-wide" dir="ltr">{success.order_number}</p>
            <button
              type="button"
              onClick={() => setSuccess(null)}
              className="w-full bg-orange-500 hover:bg-orange-600 active:scale-[0.98] transition-all text-white font-black py-4 rounded-2xl text-lg shadow-lg shadow-orange-500/30"
              data-testid="lior-new-order"
            >
              הזמנה חדשה
            </button>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-3" data-testid="lior-form">
            {fields.map(f => (
              <label key={f.key} className="block">
                <span className="block text-[12px] font-black text-orange-200/90 mb-1.5 uppercase tracking-wider">{f.label}{f.required && <span className="text-orange-500 mr-1">*</span>}</span>
                {f.textarea ? (
                  <textarea
                    value={form[f.key]}
                    onChange={(e) => setForm({ ...form, [f.key]: e.target.value })}
                    placeholder={f.placeholder}
                    rows={3}
                    className="w-full bg-stone-900/70 border border-stone-700 focus:border-orange-500 focus:ring-2 focus:ring-orange-500/30 outline-none rounded-2xl px-4 py-3.5 text-white placeholder-stone-500 text-base transition-all"
                    data-testid={`lior-field-${f.key}`}
                  />
                ) : (
                  <input
                    type={f.type || 'text'}
                    value={form[f.key]}
                    onChange={(e) => setForm({ ...form, [f.key]: e.target.value })}
                    placeholder={f.placeholder}
                    autoComplete={f.autoComplete}
                    dir={f.dir || 'auto'}
                    inputMode={f.type === 'tel' ? 'tel' : undefined}
                    className="w-full bg-stone-900/70 border border-stone-700 focus:border-orange-500 focus:ring-2 focus:ring-orange-500/30 outline-none rounded-2xl px-4 py-4 text-white placeholder-stone-500 text-base transition-all"
                    data-testid={`lior-field-${f.key}`}
                  />
                )}
              </label>
            ))}
            <button
              type="submit"
              disabled={submitting}
              className="relative w-full mt-2 py-5 rounded-2xl font-black text-xl uppercase tracking-wider transition-all active:scale-[0.98] disabled:opacity-50 overflow-hidden"
              style={{
                background: submitting ? '#444' : 'linear-gradient(135deg,#ff8a3d 0%,#ff5b00 100%)',
                boxShadow: '0 10px 40px -10px rgba(255,90,0,0.6), inset 0 1px 0 rgba(255,255,255,0.2)',
              }}
              data-testid="lior-submit"
            >
              <span className="relative z-10 flex items-center justify-center gap-2">
                {submitting ? (
                  <>
                    <svg className="w-5 h-5 animate-spin" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="3" />
                      <path className="opacity-90" fill="currentColor" d="M4 12a8 8 0 018-8v4l3-3-3-3v4a8 8 0 100 16v-4l-3 3 3 3v-4a8 8 0 01-8-8z" />
                    </svg>
                    שולח...
                  </>
                ) : (
                  <>
                    שלח הזמנה
                    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}><path strokeLinecap="round" strokeLinejoin="round" d="M14 5l7 7m0 0l-7 7m7-7H3" /></svg>
                  </>
                )}
              </span>
            </button>
            <p className="text-center text-[11px] text-stone-500 mt-3">לאחר השליחה ההזמנה נכנסת ישירות לדשבורד · ליאור · משלוח HFD</p>
          </form>
        )}

        {/* Tiny PWA install hint */}
        <p className="mt-10 text-center text-[10px] text-stone-600 uppercase tracking-widest">
          💪 הוסף למסך הבית · iOS: שתף ↗ → הוסף · Android: ⋮ → התקן אפליקציה
        </p>
      </div>
    </div>
  );
};

export default LiorOrder;
